import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Context that a widget exposes to Claude.
 * Widgets decide what's relevant - this is intentionally loose.
 */
export interface WidgetContext {
  widgetId: string;
  widgetType: string;
  focused?: boolean;

  // File-based widgets should include this
  path?: string;

  // Arbitrary widget-specific state
  // Widget decides what Claude needs to know
  state: Record<string, any>;
}

/**
 * Interface for widgets that want to expose context to Claude.
 * Implement this to make your widget Claude-aware.
 */
export interface ClaudeAwareWidget {
  /**
   * Return the current context Claude should know about.
   * Include whatever state is relevant - file paths, view bounds,
   * selections, filters, scroll position, etc.
   *
   * This is intentionally loose. Widget developers decide what matters.
   */
  getContextForClaude(): WidgetContext;
}

/**
 * Type guard to check if a component implements ClaudeAwareWidget
 */
export function isClaudeAware(component: any): component is ClaudeAwareWidget {
  return component && typeof component.getContextForClaude === 'function';
}

/**
 * Service to collect and format widget context for injection into chat messages.
 *
 * Widgets register their context, and this service aggregates it into XML
 * that gets prepended to user messages so Claude knows what the user is looking at.
 *
 * Design philosophy:
 * - Widgets are the authority on what state matters
 * - File-based interaction model: Claude writes files, widgets watch and react
 * - Context is read-only for Claude; actions happen through file operations
 */
@Injectable({
  providedIn: 'root'
})
export class WidgetContextService {
  private contexts = new Map<string, WidgetContext>();
  private focusedWidgetId: string | null = null;
  private contextSubject = new BehaviorSubject<Map<string, WidgetContext>>(this.contexts);

  public context$ = this.contextSubject.asObservable();

  constructor() {}

  /**
   * Register or update a widget's context.
   * Call this whenever the widget's relevant state changes.
   */
  updateContext(context: WidgetContext): void {
    this.contexts.set(context.widgetId, {
      ...context,
      focused: context.widgetId === this.focusedWidgetId
    });
    this.emit();
  }

  /**
   * Remove a widget's context when it closes.
   */
  removeContext(widgetId: string): void {
    this.contexts.delete(widgetId);
    if (this.focusedWidgetId === widgetId) {
      this.focusedWidgetId = null;
    }
    this.emit();
  }

  /**
   * Mark a widget as focused/active.
   */
  setFocused(widgetId: string): void {
    this.focusedWidgetId = widgetId;

    // Update focused flags on all contexts
    for (const [id, context] of this.contexts) {
      this.contexts.set(id, {
        ...context,
        focused: id === widgetId
      });
    }
    this.emit();
  }

  /**
   * Get all current widget contexts.
   */
  getContexts(): WidgetContext[] {
    return Array.from(this.contexts.values());
  }

  /**
   * Get the focused widget's context, if any.
   */
  getFocusedContext(): WidgetContext | null {
    if (!this.focusedWidgetId) return null;
    return this.contexts.get(this.focusedWidgetId) || null;
  }

  /**
   * Format all widget contexts as XML for injection into chat messages.
   *
   * Output format:
   * <widget-context>
   *   <widget type="MonacoWidget" path="/home/user/file.py" focused="true">
   *     <cursor-line>42</cursor-line>
   *     <cursor-column>10</cursor-column>
   *     <dirty>true</dirty>
   *   </widget>
   *   <widget type="RedditWidget" path="/home/team/reddit/engineering/posts/2026-02-05_example.md">
   *     <forum>engineering</forum>
   *     <post-title>Example Post</post-title>
   *     <comment-count>5</comment-count>
   *   </widget>
   * </widget-context>
   */
  formatAsXml(): string {
    if (this.contexts.size === 0) {
      return '';
    }

    const lines: string[] = ['<widget-context>'];

    for (const context of this.contexts.values()) {
      const attrs: string[] = [`type="${context.widgetType}"`];

      if (context.path) {
        attrs.push(`path="${this.escapeXml(context.path)}"`);
      }
      if (context.focused) {
        attrs.push('focused="true"');
      }

      // If state is empty, use self-closing tag
      if (Object.keys(context.state).length === 0) {
        lines.push(`  <widget ${attrs.join(' ')} />`);
      } else {
        lines.push(`  <widget ${attrs.join(' ')}>`);

        // Render state as child elements
        for (const [key, value] of Object.entries(context.state)) {
          const xmlKey = this.toKebabCase(key);
          const xmlValue = this.formatValue(value);
          lines.push(`    <${xmlKey}>${xmlValue}</${xmlKey}>`);
        }

        lines.push('  </widget>');
      }
    }

    lines.push('</widget-context>');
    return lines.join('\n');
  }

  private emit(): void {
    this.contextSubject.next(new Map(this.contexts));
  }

  private escapeXml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  private toKebabCase(str: string): string {
    return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  }

  private formatValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'object') {
      return this.escapeXml(JSON.stringify(value));
    }
    return this.escapeXml(String(value));
  }
}
